import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { clientsApi } from '@/services/api';
import { HiOutlineArrowLeft, HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi';

interface Client {
  id: string;
  clientNumber: string;
  lastName: string;
  firstName: string;
  lastNameKana: string;
  firstNameKana: string;
  birthDate: string;
  gender: string;
  postalCode: string;
  prefecture: string;
  city: string;
  address: string;
  phone: string;
  mobilePhone: string;
  email: string;
  disabilityType: string;
  disabilityGrade: string;
  supportCategory: number;
  recipientNumber: string;
  recipientValidFrom: string;
  recipientValidTo: string;
  upperLimitAmount: number;
  contractDate: string;
  emergencyContactName: string;
  emergencyContactRelationship: string;
  emergencyContactPhone: string;
  status: string;
  notes: string;
}

export default function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: client, isLoading } = useQuery<Client>({
    queryKey: ['clients', id],
    queryFn: () => clientsApi.get(id!) as Promise<Client>,
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: () => clientsApi.delete(id!),
    onSuccess: () => {
      toast.success('利用者を削除しました');
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      navigate('/clients');
    },
    onError: () => {
      toast.error('削除に失敗しました');
    },
  });

  const handleDelete = () => {
    if (confirm('この利用者を削除しますか？')) {
      deleteMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">読み込み中...</p>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">利用者が見つかりません</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/clients')} className="btn-ghost">
            <HiOutlineArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {client.lastName} {client.firstName}
            </h1>
            <p className="text-gray-600">
              {client.lastNameKana} {client.firstNameKana}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={`/clients/${id}/edit`} className="btn-secondary">
            <HiOutlinePencil className="w-5 h-5 mr-2" />
            編集
          </Link>
          <button onClick={handleDelete} className="btn-danger">
            <HiOutlineTrash className="w-5 h-5 mr-2" />
            削除
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 基本情報 */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">基本情報</h2>
          </div>
          <div className="card-body space-y-4">
            <InfoRow label="利用者番号" value={client.clientNumber} />
            <InfoRow label="生年月日" value={client.birthDate} />
            <InfoRow label="性別" value={client.gender === 'male' ? '男性' : client.gender === 'female' ? '女性' : 'その他'} />
            <InfoRow label="電話番号" value={client.phone} />
            <InfoRow label="携帯電話" value={client.mobilePhone} />
            <InfoRow label="メール" value={client.email} />
            <InfoRow label="住所" value={`${client.postalCode} ${client.prefecture}${client.city}${client.address}`} />
          </div>
        </div>

        {/* 障害・受給者証情報 */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">障害・受給者証情報</h2>
          </div>
          <div className="card-body space-y-4">
            <InfoRow label="障害種別" value={client.disabilityType} />
            <InfoRow label="障害等級" value={client.disabilityGrade} />
            <InfoRow label="障害支援区分" value={client.supportCategory ? `区分${client.supportCategory}` : ''} />
            <InfoRow label="受給者番号" value={client.recipientNumber} />
            <InfoRow label="受給者証有効期間" value={`${client.recipientValidFrom} 〜 ${client.recipientValidTo}`} />
            <InfoRow label="負担上限月額" value={client.upperLimitAmount ? `${client.upperLimitAmount.toLocaleString()}円` : ''} />
          </div>
        </div>

        {/* 緊急連絡先 */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">緊急連絡先</h2>
          </div>
          <div className="card-body space-y-4">
            <InfoRow label="氏名" value={client.emergencyContactName} />
            <InfoRow label="続柄" value={client.emergencyContactRelationship} />
            <InfoRow label="電話番号" value={client.emergencyContactPhone} />
          </div>
        </div>

        {/* 契約情報 */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">契約情報</h2>
          </div>
          <div className="card-body space-y-4">
            <InfoRow label="契約日" value={client.contractDate} />
            <InfoRow label="状態" value={client.status} />
          </div>
        </div>

        {/* 備考 */}
        {client.notes && (
          <div className="card lg:col-span-2">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900">備考</h2>
            </div>
            <div className="card-body">
              <p className="text-gray-700 whitespace-pre-wrap">{client.notes}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string | undefined }) {
  return (
    <div className="flex">
      <dt className="w-32 flex-shrink-0 text-sm text-gray-500">{label}</dt>
      <dd className="text-sm text-gray-900">{value || '-'}</dd>
    </div>
  );
}


